import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IContentPageResponse } from '@modeso/dgoods-lib-admin-fe/models/content.pages';
import { IContentBoxResponse, INewstickerResponse, ISpotLight } from '@modeso/types__dgoods-products';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as fromActions from '../actions/products.actions';
import { InternationalTopUpOfferItem } from '../models/international-top-up-offer-item.interface';
import { InternationalTopUpOffersOrOperators } from '../models/international-top-up-offers-or-operators.interface';
import { InternationalTopUpPhoneContext } from '../models/international-top-up-phone-context.interface';
import { InternationalTopUpSelectedCarrier } from '../models/international-top-up-selected-carrier.interface';
import { InternationalTopUpRequestDto } from '../models/international-top-up.request.dto';
import { ShopCategory } from '../models/shop-category.interface';
import { ShopProductLine } from '../models/shopProductLine.interface';
import * as fromFeature from './../reducers/products.reducer';

@Injectable({ providedIn: 'root' })
export class ProductStoreProvider {
    constructor(public store: Store<fromFeature.AppState>) {}

    public fetchAllProducts(): void {
        this.store.dispatch(fromActions.getAllProducts());
    }

    public getShopProductLines$(): Observable<ShopProductLine[]> {
        return this.store.pipe(select(fromFeature.selectFeatureProducts));
    }

    public getProduct$(productLineId: string): Observable<ShopProductLine | null> {
        return this.store.pipe(
            select(fromFeature.selectProductById(productLineId)),
            map((product) => {
                if (product != null) {
                    return product;
                }
                const savedProducts = JSON.parse(localStorage.getItem('products'));

                if (savedProducts?.length !== 0) {
                    return savedProducts.find((x) => x.productLineId === productLineId);
                }

                return null;
            }),
        );
    }

    public getCategories$(): Observable<ShopCategory[]> {
        return this.store.pipe(select(fromFeature.selectFeatureCategories));
    }

    public getActiveFilter$(): Observable<string> {
        return this.store.pipe(select(fromFeature.selectActiveCategoryFilter));
    }

    public setFilter(category: string): void {
        this.store.dispatch(fromActions.setActiveCategoryFilter({ payload: category }));
    }

    public setLandingPageLoadingState(isLoading: boolean): void {
        this.store.dispatch(fromActions.setLandingPageLoadingState({ payload: isLoading }));
    }

    public getLandingBuyLoadingState$(): Observable<boolean> {
        return this.store.pipe(select(fromFeature.selectLandingBuyLoadingState));
    }

    public getTermsAndConditions$(productLineId: string): Observable<string | null> {
        return this.store.pipe(
            select(fromFeature.selectProductById(productLineId)),
            map((product) => {
                if (product != null) {
                    return product.termsAndConditions;
                }
                const savedProducts = JSON.parse(localStorage.getItem('products'));

                if (savedProducts?.length !== 0) {
                    const foundSavedProduct = savedProducts.find(
                        (savedProduct) => savedProduct.productLineId === productLineId,
                    );

                    if (foundSavedProduct != null) {
                        return foundSavedProduct.termsAndConditions;
                    }
                    return null;
                }

                return null;
            }),
        );
    }

    public getNewsTicker(status: any): Observable<INewstickerResponse> {
        this.store.dispatch(fromActions.getNewsTicker({ payload: status }));
        return this.store.pipe(select(fromFeature.selectFeatureNewsTicker));
    }

    public selectNewsTicker(): Observable<INewstickerResponse> {
        return this.store.pipe(select(fromFeature.selectFeatureNewsTicker));
    }

    public getMenuItems$(): Observable<any> {
        this.store.dispatch(fromActions.getAllContentPages());
        return this.store.pipe(select(fromFeature.selectMenuItems));
    }
    public getAllContentPages$(): Observable<IContentPageResponse[]> {
        this.store.dispatch(fromActions.getAllContentPages());
        return this.store.pipe(select(fromFeature.selectFeatureContentPagesList));
    }
    public selectAllContentPages$(): Observable<IContentPageResponse[]> {
        return this.store.pipe(select(fromFeature.selectFeatureContentPagesList));
    }

    public getSpotlights$(): void {
        this.store.dispatch(fromActions.getSpotlights());
    }

    public selectSpotlightFromStore$(): Observable<ISpotLight[]> {
        return this.store.pipe(select(fromFeature.selectSpotlights));
    }

    public selectUserHasSpotlightsFromStore$(): Observable<boolean> {
        return this.store.pipe(select(fromFeature.selectUserHasSpotlights));
    }

    public markSpotlightAsRead$(spotlightId): void {
        this.store.dispatch(fromActions.markSpotlightAsRead({ payload: spotlightId }));
    }

    public getPublishedContentBox$(productLineId: string): Observable<IContentBoxResponse> {
        this.store.dispatch(fromActions.getPublishedContentBox({ payload: productLineId }));
        return this.store.pipe(select(fromFeature.selectFeatureContentBox));
    }

    public lookupInternationalTopUpOffersOrOperators(payload: InternationalTopUpRequestDto): void {
        this.store.dispatch(fromActions.lookupInternationalTopUpOffersOrOperators({ payload }));
    }

    public selectInternationalTopUpLookupResult$(): Observable<InternationalTopUpOffersOrOperators | undefined> {
        return this.store.select(fromFeature.selectInternationalTopUpLookupResult);
    }

    public setInternationalTopUpPhoneContext(payload: InternationalTopUpPhoneContext): void {
        this.store.dispatch(fromActions.setInternationalTopUpPhoneContext({ payload }));
    }

    public selectInternationalTopUpPhoneContext$(): Observable<InternationalTopUpPhoneContext | undefined> {
        return this.store.select(fromFeature.selectInternationalTopUpPhoneContext);
    }

    public setInternationalTopUpSelectedOffer(payload: InternationalTopUpOfferItem): void {
        this.store.dispatch(fromActions.setInternationalTopUpSelectedOffer({ payload }));
    }

    public selectInternationalTopUpSelectedOffer$(): Observable<InternationalTopUpOfferItem | undefined> {
        return this.store.select(fromFeature.selectInternationalTopUpSelectedOffer);
    }

    public setInternationalTopUpSelectedCarrier(payload: InternationalTopUpSelectedCarrier): void {
        this.store.dispatch(fromActions.setInternationalTopUpSelectedCarrier({ payload }));
    }

    public selectInternationalTopUpSelectedCarrier$(): Observable<InternationalTopUpSelectedCarrier | undefined> {
        return this.store.select(fromFeature.selectInternationalTopUpSelectedCarrier);
    }

    public selectProductsFeatureError$(): Observable<HttpErrorResponse | undefined> {
        return this.store.select(fromFeature.selectFeatureError);
    }

    public getInternationalTopUpLookupLoadingState$(): Observable<boolean> {
        return this.store.select(fromFeature.selectInternationalTopUpLookupLoadingState);
    }
}
